import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'config/axiosBaseQuery';
import { endpoints } from 'config/api';
import {
  Expense,
  CreateExpenseDto,
  UpdateExpenseDto,
  ExpenseQueryParams,
  ExpenseStats,
  ProjectExpensesResponse,
} from './expensesTypes';
import { accountingApi } from 'features/accounting/accountingApi';

export const expensesApi = createApi({
  reducerPath: 'expensesApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Expenses', 'Expense', 'ExpenseStats'],
  endpoints: (builder) => ({
    // ==================
    // Expenses List
    // ==================

    getExpenses: builder.query<Expense[], ExpenseQueryParams | void>({
      query: (params) => ({
        url: endpoints.expenses.base,
        method: 'GET',
        params: params ? {
          startDate: params.startDate,
          endDate: params.endDate,
          category: params.category,
          type: params.type,
          projectId: params.projectId,
          isRecurring: params.isRecurring,
        } : undefined,
      }),
      transformResponse: (response: { success: boolean; data: Expense[] }) => response.data,
      providesTags: ['Expenses'],
    }),

    // ==================
    // Expense Stats
    // ==================

    getExpenseStats: builder.query<ExpenseStats, { startDate: string; endDate: string }>({
      query: ({ startDate, endDate }) => ({
        url: endpoints.expenses.stats,
        method: 'GET',
        params: { startDate, endDate },
      }),
      transformResponse: (response: { success: boolean; data: ExpenseStats }) => response.data,
      providesTags: ['ExpenseStats'],
    }),

    // ==================
    // Project Expenses
    // ==================

    getProjectExpenses: builder.query<ProjectExpensesResponse, string>({
      query: (projectId) => ({
        url: endpoints.expenses.byProject(projectId),
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: ProjectExpensesResponse }) => response.data,
      providesTags: (_res, _err, projectId) => [
        { type: 'Expenses', id: `project-${projectId}` },
        'Expenses',
      ],
    }),

    // ==================
    // Single Expense Operations
    // ==================

    getExpense: builder.query<Expense, string>({
      query: (id) => ({
        url: endpoints.expenses.byId(id),
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: Expense }) => response.data,
      providesTags: (_res, _err, id) => [{ type: 'Expense', id }],
    }),

    createExpense: builder.mutation<Expense, CreateExpenseDto>({
      query: (data) => ({
        url: endpoints.expenses.base,
        method: 'POST',
        data,
      }),
      transformResponse: (response: { success: boolean; data: Expense }) => response.data,
      invalidatesTags: ['Expenses', 'ExpenseStats'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        // Invalidate accounting API cache to refresh stats
        dispatch(accountingApi.util.invalidateTags(['AccountingOverview']));
      },
    }),

    updateExpense: builder.mutation<Expense, { id: string; data: UpdateExpenseDto; reason: string }>({
      query: ({ id, data, reason }) => ({
        url: endpoints.expenses.byId(id),
        method: 'PUT',
        data: { ...data, reason },
      }),
      transformResponse: (response: { success: boolean; data: Expense }) => response.data,
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Expense', id },
        'Expenses',
        'ExpenseStats',
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        // Invalidate accounting API cache to refresh stats
        dispatch(accountingApi.util.invalidateTags(['AccountingOverview']));
      },
    }),

    deleteExpense: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: endpoints.expenses.byId(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['Expenses', 'ExpenseStats'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(accountingApi.util.invalidateTags(['AccountingOverview']));
      },
    }),

    invalidateExpense: builder.mutation<Expense, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: endpoints.expenses.invalidate(id),
        method: 'PATCH',
        data: { reason },
      }),
      transformResponse: (response: { success: boolean; data: Expense }) => response.data,
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Expense', id },
        'Expenses',
        'ExpenseStats',
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        dispatch(accountingApi.util.invalidateTags(['AccountingOverview']));
      },
    }),
  }),
});

export const {
  useGetExpensesQuery,
  useGetExpenseStatsQuery,
  useGetProjectExpensesQuery,
  useGetExpenseQuery,
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
  useDeleteExpenseMutation,
  useInvalidateExpenseMutation,
} = expensesApi;

